import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { listMembers, type MemberView } from '../settings/api';
import { Avatar } from './Avatar';

/**
 * Choosing a person, with their face.
 *
 * A native `<select>` cannot do this. `<option>` accepts text and nothing else — no image, no second
 * line — which is why every product that shows avatars in a picker has built one of these. So this
 * is a real listbox: a button that shows the current choice, a popup that lists people with their
 * pictures, and a filter box once the list is long enough to need one.
 *
 * Giving that up means re-implementing what the browser gave away, so the keyboard is done properly
 * rather than approximately: arrows move, Home and End jump, Enter takes, Escape closes and returns
 * focus to the button, and typing filters. A picker that can only be used with a mouse is a picker
 * that excludes people, and on a screen somebody fills in forty times a day it is also just slow.
 */

/** Below this, the filter box is noise; above it, scrolling to find somebody is worse. */
const FILTER_THRESHOLD = 7;

/** Roughly what the popup occupies at full height, for deciding which way to open. Matches the CSS. */
const POPUP_MAX_HEIGHT = 320;

export interface UserOption {
  id: string;
  name: string;
  avatarUrl?: string | null;
  secondary?: string | null;
}

export function membersToOptions(members: readonly MemberView[]): UserOption[] {
  return members
    .filter((member) => member.status === 'ACTIVE')
    .map((member) => ({
      id: member.user_id,
      name: member.name,
      avatarUrl: member.avatar_url,
      secondary: member.email,
    }));
}

/** The team, cached across every picker on the page. */
export function useMembers(enabled = true): { options: UserOption[]; isLoading: boolean } {
  const members = useQuery({
    queryKey: ['members'],
    queryFn: listMembers,
    staleTime: 5 * 60 * 1000,
    enabled,
  });

  const options = useMemo(
    () => membersToOptions(members.data?.members ?? []),
    [members.data?.members],
  );

  return { options, isLoading: members.isLoading };
}

export function UserSelect({
  value,
  onChange,
  options,
  placeholder,
  emptyLabel,
  allowEmpty = true,
  disabled = false,
  id,
}: {
  value: string | null;
  onChange: (next: string | null) => void;
  options: readonly UserOption[];
  /** What the button says with nothing chosen. */
  placeholder?: string;
  /** What the "nobody" row says. Defaults to the placeholder. */
  emptyLabel?: string;
  allowEmpty?: boolean;
  disabled?: boolean;
  id?: string;
}): React.JSX.Element {
  const generatedId = useId();
  const listId = `${id ?? generatedId}-list`;

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState(0);
  const [up, setUp] = useState(false);

  const button = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);

  const chosen = options.find((option) => option.id === value) ?? null;
  const nobody = placeholder ?? t('people.unassigned');
  const showFilter = options.length >= FILTER_THRESHOLD;

  const matches = useMemo(() => {
    const term = filter.trim().toLowerCase();

    if (term === '') return options;

    return options.filter(
      (option) =>
        option.name.toLowerCase().includes(term) ||
        (option.secondary ?? '').toLowerCase().includes(term),
    );
  }, [options, filter]);

  // Row 0 is "nobody" when it is offered, so the people start at 1.
  const rows: (UserOption | null)[] = useMemo(
    () => (allowEmpty ? [null, ...matches] : [...matches]),
    [allowEmpty, matches],
  );

  // What the list looked like when the popup opened, for placing the cursor on the current choice.
  // Read through a ref so the opening effect does not depend on a value that changes as you type.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const currentRows = rowsRef.current;

  useEffect(() => {
    if (!open) return;

    setFilter('');
    setActive(
      Math.max(
        0,
        currentRows.findIndex((row) => (row?.id ?? null) === value),
      ),
    );

    /*
     * Open upwards when there is no room below.
     *
     * The popup is absolutely positioned, so near the bottom of a dialog or a short viewport it is
     * simply clipped — and a list whose last three people cannot be reached is worse than a list
     * that opens the other way. Measured against the viewport rather than the nearest scroll
     * container because the button's own rectangle already accounts for wherever it is.
     */
    const rect = button.current?.getBoundingClientRect();

    if (rect) {
      const below = window.innerHeight - rect.bottom;
      setUp(below < POPUP_MAX_HEIGHT && rect.top > below);
    }

    // Focus the filter when there is one, otherwise the list itself, so the first keypress lands
    // somewhere useful instead of on the page behind.
    const target = showFilter ? search.current : popup.current;
    target?.focus();
    // Deliberately only on open. `rows` changes with every keystroke and re-running would reset the
    // filter the moment somebody typed into it.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above: opening is the event
  }, [open, showFilter, value]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;

      if (popup.current?.contains(target) || button.current?.contains(target)) return;

      setOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);

    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const close = (focusButton = true): void => {
    setOpen(false);
    if (focusButton) button.current?.focus();
  };

  const take = (row: UserOption | null): void => {
    onChange(row?.id ?? null);
    close();
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }

    if (event.key === 'Enter' || (event.key === ' ' && !showFilter)) {
      event.preventDefault();
      if (rows.length > 0) take(rows[Math.min(active, rows.length - 1)] ?? null);
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((current) => Math.min(current + 1, rows.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      setActive(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      setActive(Math.max(0, rows.length - 1));
    }
  };

  return (
    <div className="userselect">
      <button
        ref={button}
        type="button"
        id={id}
        className="userselect__button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((current) => !current)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            setOpen(true);
          }
        }}
      >
        {chosen ? (
          <span className="person">
            <Avatar name={chosen.name} url={chosen.avatarUrl} size="xs" title={null} />
            <span className="person__name">{chosen.name}</span>
          </span>
        ) : (
          <span className="muted">{nobody}</span>
        )}

        <span className="userselect__caret" aria-hidden="true">
          ▾
        </span>
      </button>

      {open && (
        <div
          className={`userselect__popup${up ? ' userselect__popup--up' : ''}`}
          ref={popup}
          tabIndex={-1}
          onKeyDown={onKeyDown}
        >
          {showFilter && (
            <input
              ref={search}
              type="search"
              className="userselect__filter"
              value={filter}
              placeholder={t('people.filter')}
              onChange={(event) => {
                setFilter(event.target.value);
                setActive(0);
              }}
            />
          )}

          {/*
           * Said alongside the list, not instead of it.
           *
           * "Nobody" stays on offer even when the filter matches no one — clearing an assignment is
           * still a valid thing to want — but a popup showing only that row, with no word about the
           * search, reads as though the team has one member called "Sem responsável".
           */}
          {matches.length === 0 && filter.trim() !== '' && (
            <p className="userselect__none muted">{t('people.noMatches')}</p>
          )}

          <ul className="userselect__list" id={listId} role="listbox">
            {rows.length === 0 ? (
              <li className="userselect__none muted">{t('people.noMatches')}</li>
            ) : (
              rows.map((row, index) => (
                <li key={row?.id ?? '__nobody__'}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={(row?.id ?? null) === value}
                    className={`userselect__option${index === active ? ' is-active' : ''}${
                      (row?.id ?? null) === value ? ' is-chosen' : ''
                    }`}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => take(row)}
                  >
                    {row === null ? (
                      <span className="muted">{emptyLabel ?? nobody}</span>
                    ) : (
                      <span className="person">
                        <Avatar name={row.name} url={row.avatarUrl} size="sm" title={null} />
                        <span className="person__text">
                          <span className="person__name">{row.name}</span>
                          {row.secondary && (
                            <span className="person__secondary">{row.secondary}</span>
                          )}
                        </span>
                      </span>
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * The common case: pick somebody from this tenant's team.
 *
 * Wraps the shared `['members']` query, so the twelve pickers in this product do not become twelve
 * requests — React Query dedupes them and the avatars arrive once.
 */
export function MemberSelect(
  props: Omit<Parameters<typeof UserSelect>[0], 'options'> & { extra?: readonly UserOption[] },
): React.JSX.Element {
  const { extra, ...rest } = props;
  const { options } = useMembers(!rest.disabled);

  const all = useMemo(() => [...(extra ?? []), ...options], [extra, options]);

  return <UserSelect {...rest} options={all} />;
}
